import React from "react";
import { Navigation, Droplet, Clock, Star } from "lucide-react";
import { Price } from "../../services/searchService";
import { calculateFare, calculateMultiCityFare, FareLeg } from "../../utils/fareCalculator";
import { getCarDisplayName, getFuelTypeDisplay } from "../../utils/vehicleDisplay";

interface CarDetailsProps {
  price: Price;
  onSelect: (id: string) => void;
  distanceKm?: number;
  fareLegs?: FareLeg[];
  pickupTime: string;
  sourceState?: string;
  departureDate?: string;
  returnDate?: string;
  carImage?: string;
  isAvailable?: boolean;
}

const CarDetailsCard: React.FC<CarDetailsProps> = ({
  price,
  onSelect,
  distanceKm,
  fareLegs,
  pickupTime,
  sourceState,
  departureDate,
  returnDate,
  carImage = "/api/placeholder/300/200", // Default placeholder image
  isAvailable = true,
}) => {
  const fare =
    fareLegs && fareLegs.length > 0
      ? calculateMultiCityFare(price, fareLegs, pickupTime, sourceState, departureDate, returnDate)
      : calculateFare(price, distanceKm, pickupTime, sourceState, departureDate, returnDate);

  const formatPrice = (priceValue: number) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
    }).format(priceValue);
  };

  const getCarName = () => getCarDisplayName(price);

  const getCarFeatures = () => {
    const features = [];
    features.push(getFuelTypeDisplay(price.fuel_type));
    if (price.is_admin) features.push("Verified");
    features.push(`${price.free_waiting_minutes} min free waiting`);
    return features;
  };

  return (
    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-4 sm:p-6 border border-gray-200 bg-white rounded-lg shadow-sm w-full relative hover:shadow-md transition-shadow duration-200">
      {/* Left side - Car Image */}
      <div className="w-full sm:w-1/3 sm:flex-shrink-0">
        <img
          src={carImage}
          alt={`${getCarName()} car`}
          className="object-cover w-full h-40 sm:h-auto sm:object-contain rounded-lg"
        />
      </div>

      {/* Right side - Car details */}
      <div className="w-full sm:w-2/3 sm:pl-6">
        {/* Car name and verification */}
        <div className="flex flex-wrap justify-between items-center gap-2 mb-1">
          <div className="flex items-center flex-wrap">
            <h2 className="text-xl font-bold text-[#06080C]">{getCarName()}</h2>
            <span className="text-gray-500 ml-2">or similar</span>
          </div>
          <div className="flex items-center">
            {price.is_admin && (
              <div className="flex items-center bg-[#06080C] text-white px-2 py-1 rounded-md">
                <Star className="w-4 h-4 mr-1" />
                <span className="font-bold">Verified</span>
              </div>
            )}
          </div>
        </div>

        {/* Car features */}
        <div className="flex flex-wrap items-center gap-y-1 text-sm text-gray-600 mb-3">
          {getCarFeatures().map((feature, index) => (
            <React.Fragment key={index}>
              {index > 0 && <span className="mx-2">•</span>}
              <span>{feature}</span>
            </React.Fragment>
          ))}
        </div>

        {/* Price display */}
        <div className="font-extrabold text-2xl mb-3 text-[#06080C]">
          {formatPrice(fare.totalFare)}
        </div>
        <div className="text-xs text-gray-500 mb-2">
          For {fare.distanceKm.toFixed(1)} km
          {fare.isNight && " • Night charges included"}
        </div>

        {/* Car details with icons */}
        <div className="space-y-3 mb-4">
          <div className="flex items-center">
            <Navigation className="w-5 h-5 text-[#06080C] mr-3" />
            <span className="w-32">Extra km fare</span>
            <span className="font-bold">
              {formatPrice(price.extra_fare)}/km
            </span>
          </div>
          <div className="flex items-center">
            <Droplet className="w-5 h-5 text-[#06080C] mr-3" />
            <span className="w-32">Fuel Type</span>
            <span className="font-bold">{getFuelTypeDisplay(price.fuel_type)}</span>
          </div>
          <div className="flex items-center">
            <Clock className="w-5 h-5 text-[#06080C] mr-3" />
            <span className="w-32">Free Waiting</span>
            <span className="font-bold">
              {price.free_waiting_minutes} minutes
            </span>
          </div>
        </div>

        {/* Additional pricing info */}
        {fare.extraFare > 0 && (
          <div className="bg-gray-50 p-3 rounded-md mb-4 space-y-1">
            <div className="flex justify-between items-center text-sm">
              <span className="text-gray-600">
                Extra Distance ({fare.extraKm.toFixed(1)} km):
              </span>
              <span className="font-medium">{formatPrice(fare.extraFare)}</span>
            </div>
          </div>
        )}

        {/* Button + availability tag */}
        <div className="flex items-center justify-between gap-4">
          <span
            className={`px-3 py-1 text-xs rounded-md sm:absolute sm:bottom-6 sm:left-6 ${
              isAvailable
                ? "bg-green-100 text-green-800"
                : "bg-gray-200 text-gray-600"
            }`}
          >
            {isAvailable ? "Available" : "Unavailable"}
          </span>
          <button
            className={`text-white py-3 px-8 sm:px-16 rounded-md font-medium transition-colors duration-200 ${
              isAvailable
                ? "bg-[#06080C] hover:bg-black"
                : "bg-gray-400 cursor-not-allowed"
            }`}
            onClick={() => isAvailable && onSelect(price.id)}
            disabled={!isAvailable}
          >
            {isAvailable ? "Book Now" : "Unavailable"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CarDetailsCard;
